import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getMessageByKey } from 'Stores/LanguageStore';
import { ParkingReservationColumnKeys } from './ParkingReservationColumnKeys';

export interface ColumnConfig {
	key: string;
	label: string;
	visible: boolean;
	locked?: boolean;
}

interface ColumnVisibility {
	[key: string]: boolean;
}

export type ParkingDateFilterType = 'created' | 'parking';

export interface StaffParkingReservationsFilters {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	dateFilterType: ParkingDateFilterType;
	statusFilters: string[];
	locationFilters: string[];
	orderBy: string;
	orderByDescending: boolean;
	pageNo: number;
}

interface StaffParkingReservationsState {
	filters: StaffParkingReservationsFilters;

	selectedStatuses: string[];
	selectedLocations: string[];

	// Column visibility
	columnVisibility: ColumnVisibility;
	visibleColumns: ColumnConfig[];

	// Selection state for export
	selectedReservationIds: Set<string>;

	// Export state
	isExporting: boolean;

	// Actions
	setFilters: (filters: StaffParkingReservationsFilters) => void;
	updateFilters: (partialFilters: Partial<StaffParkingReservationsFilters>) => void;
	resetFilters: () => void;
	setSelectedStatuses: (statuses: string[]) => void;
	setSelectedLocations: (locations: string[]) => void;
	setSearchString: (searchString?: string) => void;
	setVisibleColumns: (columns: ColumnConfig[]) => void;
	toggleColumn: (key: string) => void;
	setSelectedReservationIds: (ids: Set<string>) => void;
	setIsExporting: (isExporting: boolean) => void;
}

const defaultSelectedStatuses: string[] = ['ACTIVE', 'CONFIRMED', 'PENDING'];

export const defaultFilters: StaffParkingReservationsFilters = {
	statusFilters: defaultSelectedStatuses,
	locationFilters: [],
	dateFilterType: 'created',
	orderBy: 'CREATED_DATE',
	orderByDescending: true,
	pageNo: 0,
	searchString: '',
};

// Default columns configuration
const getDefaultColumns = (): ColumnConfig[] => [
	{ key: ParkingReservationColumnKeys.HUMAN_READABLE_ID, label: 'Reservation ID', visible: true, locked: true },
	{ key: ParkingReservationColumnKeys.CREATED_DATE, label: 'Created', visible: true },
	{ key: ParkingReservationColumnKeys.CUSTOMER, label: 'Customer', visible: true },
	{ key: ParkingReservationColumnKeys.LOCATION, label: 'Location', visible: true },
	{
		key: ParkingReservationColumnKeys.REGISTRATION,
		label: getMessageByKey('vehicle-tab__rego__label', 'Rego') ?? 'Rego',
		visible: true,
	},
	{ key: ParkingReservationColumnKeys.START_DATE, label: 'Start Date', visible: true },
	{ key: ParkingReservationColumnKeys.END_DATE, label: 'End Date', visible: true },
	{ key: ParkingReservationColumnKeys.TOTAL_PRICE, label: 'Total', visible: true },
	{ key: ParkingReservationColumnKeys.TRANSACTION_ID, label: 'Transaction ID', visible: true },
	{ key: ParkingReservationColumnKeys.STATUS, label: 'Status', visible: true },
];

// Helper to merge visibility with default columns
const buildColumns = (visibility: ColumnVisibility): ColumnConfig[] => {
	const defaults = getDefaultColumns();

	return defaults.map(col => ({
		...col,
		visible: visibility[col.key] ?? col.visible,
	}));
};

// Custom serializer to handle Date objects
const customStorage = {
	getItem: (name: string) => {
		const str = sessionStorage.getItem(name);
		if (!str) return null;

		const parsed = JSON.parse(str);
		if (parsed.state?.filters?.startDate) {
			parsed.state.filters.startDate = new Date(parsed.state.filters.startDate);
		}
		if (parsed.state?.filters?.endDate) {
			parsed.state.filters.endDate = new Date(parsed.state.filters.endDate);
		}

		return JSON.stringify(parsed);
	},
	setItem: (name: string, value: string) => {
		sessionStorage.setItem(name, value);
	},
	removeItem: (name: string) => sessionStorage.removeItem(name),
};

export const useStaffParkingReservationsStore = create<StaffParkingReservationsState>()(
	persist(
		(set, get) => ({
			filters: defaultFilters,
			selectedStatuses: defaultSelectedStatuses,
			selectedLocations: [],
			columnVisibility: {},
			visibleColumns: getDefaultColumns(),
			selectedReservationIds: new Set<string>(),
			isExporting: false,

			setFilters: (filters: StaffParkingReservationsFilters) => {
				set({
					filters,
					selectedStatuses: filters.statusFilters,
					selectedLocations: filters.locationFilters,
				});
			},

			updateFilters: (partialFilters: Partial<StaffParkingReservationsFilters>) => {
				const currentFilters = get().filters;
				const newFilters = { ...currentFilters, ...partialFilters };

				set({
					filters: newFilters,
					selectedStatuses: newFilters.statusFilters,
					selectedLocations: newFilters.locationFilters,
				});
			},

			resetFilters: () => {
				set({
					filters: defaultFilters,
					selectedStatuses: defaultSelectedStatuses,
					selectedLocations: [],
				});
			},

			setSelectedStatuses: (statuses: string[]) => {
				const currentFilters = get().filters;
				set({
					selectedStatuses: statuses,
					filters: {
						...currentFilters,
						statusFilters: statuses,
						pageNo: 0,
					},
				});
			},

			setSelectedLocations: (locations: string[]) => {
				const currentFilters = get().filters;
				set({
					selectedLocations: locations,
					filters: {
						...currentFilters,
						locationFilters: locations,
						pageNo: 0,
					},
				});
			},

			setSearchString: (searchString?: string) => {
				const currentFilters = get().filters;
				set({
					filters: {
						...currentFilters,
						searchString,
						pageNo: 0,
					},
				});
			},

			setVisibleColumns: (columns: ColumnConfig[]) => {
				const visibility: ColumnVisibility = {};
				columns.forEach(col => {
					visibility[col.key] = col.visible;
				});
				set({
					visibleColumns: columns,
					columnVisibility: visibility,
				});
			},

			toggleColumn: (key: string) => {
				const currentColumns = get().visibleColumns;
				const updatedColumns = currentColumns.map(col =>
					col.key === key && !col.locked ? { ...col, visible: !col.visible } : col,
				);

				const visibility: ColumnVisibility = {};
				updatedColumns.forEach(col => {
					visibility[col.key] = col.visible;
				});

				set({
					visibleColumns: updatedColumns,
					columnVisibility: visibility,
				});
			},

			setSelectedReservationIds: (ids: Set<string>) => {
				set({ selectedReservationIds: ids });
			},

			setIsExporting: (isExporting: boolean) => {
				set({ isExporting });
			},
		}),
		{
			name: 'staff-parking-reservations-filters',
			storage: createJSONStorage(() => customStorage),
			partialize: state => ({
				filters: state.filters,
				selectedStatuses: state.selectedStatuses,
				selectedLocations: state.selectedLocations,
				columnVisibility: state.columnVisibility,
			}),
			onRehydrateStorage: () => state => {
				if (state) {
					if (state.filters) {
						state.selectedStatuses = state.filters.statusFilters || defaultSelectedStatuses;
						state.selectedLocations = state.filters.locationFilters || [];
					}
					// Rebuild columns with fresh configs and persisted visibility
					state.visibleColumns = buildColumns(state.columnVisibility || {});
				}
			},
		},
	),
);
